import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import * as imagesApi from "@api/images.api";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { useNotification } from "@context/NotificationContext";
import { ConfirmDialog } from "@components/ConfirmDialog";
import type { Image } from "@/types";
import { routes } from "@/constants/routes";

export default function ImageDetails() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { getKey, hasKey } = useLocalKeys();

  const [image, setImage] = useState<Image | null>(null);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    imagesApi
      .getById(id)
      .then(setImage)
      .catch(() =>
        notify(t("imageDetails.loadError", "Failed to load image"), "error"),
      )
      .finally(() => setLoading(false));
  }, [id, notify, t]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const handleView = useCallback(async () => {
    if (!id) return;
    setViewing(true);
    try {
      const key = await getKey(id);
      if (!key) {
        notify(t("imageDetails.noKey", "Key not found"), "error");
        return;
      }
      const blob = await imagesApi.download(id, key);
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch {
      notify(t("imageDetails.viewError", "Failed to decrypt image"), "error");
    } finally {
      setViewing(false);
    }
  }, [id, getKey, notify, t]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    try {
      await imagesApi.softDelete(id);
      notify(t("imageDetails.deleted", "Image moved to trash"), "success");
      navigate(routes.dashboard);
    } catch {
      notify(t("imageDetails.deleteError", "Failed to delete"), "error");
    }
    setDeleteOpen(false);
  }, [id, notify, navigate, t]);

  const keyExists = id ? hasKey(id) : false;

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!image) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          {t("imageDetails.notFound", "Image not found")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {image.name}
      </Typography>
      <Grid container spacing={3}>
        {/* Metadata */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("imageDetails.metadata", "Metadata")}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("imageDetails.fileName", "File Name")}
                  </Typography>
                  <Typography variant="body2">{image.name}</Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("imageDetails.fileSize", "Size")}
                  </Typography>
                  <Typography variant="body2">
                    {(image.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("imageDetails.mimeType", "Type")}
                  </Typography>
                  <Typography variant="body2">{image.mimeType}</Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("imageDetails.uploaded", "Uploaded")}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(image.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Key status + actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("imageDetails.access", "Access")}
              </Typography>

              {keyExists ? (
                <>
                  <Chip
                    icon={<LockOpenIcon />}
                    label={t("imageDetails.keyAvailable", "Key Available")}
                    color="success"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      startIcon={<ViewIcon />}
                      onClick={handleView}
                      disabled={viewing}
                    >
                      {viewing
                        ? t("imageDetails.decrypting", "Decrypting...")
                        : t("imageDetails.viewImage", "View Image")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteOpen(true)}
                    >
                      {t("imageDetails.delete", "Delete")}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2 }}>
                    {t(
                      "imageDetails.keyRequired",
                      "Encryption key not found in this browser. Import your key bundle to access this image.",
                    )}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteOpen(true)}
                  >
                    {t("imageDetails.delete", "Delete")}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Image preview */}
        {blobUrl && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box
                  component="img"
                  src={blobUrl}
                  alt={image.name}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 600,
                    display: "block",
                    mx: "auto",
                    borderRadius: 1,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Security profile */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <ShieldIcon color="primary" />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {t("imageDetails.securityProfile", "Security Profile")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t(
                  "imageDetails.encryptionInfo",
                  "This image is encrypted using AES-256-GCM. The unlock key is stored exclusively in your browser's localStorage and never transmitted to our servers in plaintext.",
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <ConfirmDialog
        open={deleteOpen}
        title={t("imageDetails.confirmDeleteTitle", "Delete Image")}
        message={t(
          "imageDetails.confirmDeleteMsg",
          "This image will be moved to trash. You can restore it within 30 days.",
        )}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
        confirmLabel={t("imageDetails.delete", "Delete")}
      />
    </Box>
  );
}
